// «Resultados de campaña» — embudo visitante → conversación → registro → pago.
//
// Fuentes (org SIEMPRE del JWT en cada backend):
//   · mcp_django GET /admin/public-tracking/campaign  → visitas, origen, WhatsApp,
//     chat del sitio, registros (CRM) y pagos (EventOrder).
//   · Orión GET /threads/inbox (party=external)       → conversaciones reales por
//     canal. Orión no filtra por fecha de inicio: contamos las que tuvieron
//     actividad dentro del periodo (last_message_at) y lo decimos en pantalla.
// Datos reales; estados vacíos honestos. Tokens semánticos (claro/oscuro).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import {
  Users, MessageCircle, MessagesSquare, UserPlus, CreditCard, BadgeCheck, RefreshCw, Info, Globe,
} from 'lucide-react';
import { fetchCampaign, fetchVisitorsList, type CampaignQuery, type CampaignSummary, type CampaignSource, type VisitorList } from '@/modules/public-tracking/api';
import { getInbox, type InboxResponse } from '@/lib/api';
import { AdsPanel } from './AdsPanel';
import { CampaignVisitors } from './CampaignVisitors';
import { JourneyDrawer, defaultJourneyLoaders, type JourneyLoaders } from './JourneyDrawer';
import { UtmLinkBuilder } from './UtmLinkBuilder';

type Period = 'today' | '7' | '30' | 'launch';
const PERIODS: { key: Period; label: string }[] = [
  { key: 'today', label: 'Hoy' },
  { key: '7', label: '7 días' },
  { key: '30', label: '30 días' },
  { key: 'launch', label: 'Desde el lanzamiento' },
];
const TZ = 'America/Mexico_City';

export type OrionConversations = { whatsapp: number; web: number; capped: boolean } | null;

export type CampaignLoaders = {
  campaign: (q: CampaignQuery) => Promise<CampaignSummary>;
  inbox: (channel: 'whatsapp' | 'web') => Promise<InboxResponse | null>;
  visitors?: (days: number) => Promise<VisitorList>;
  journey?: JourneyLoaders;
};

const loadVisitors = (days: number) => fetchVisitorsList(days, 50);

const defaultLoaders: CampaignLoaders = {
  campaign: fetchCampaign,
  inbox: (channel) => getInbox({ party: 'external', channel, limit: 500 }),
  visitors: loadVisitors,
  journey: defaultJourneyLoaders,
};

interface Props {
  addNotification?: (n: { type: string; title: string; message?: string }) => void;
  /** Solo para la vista previa de desarrollo (datos simulados). */
  loaders?: CampaignLoaders;
  initialPeriod?: Period;
  /** Solo vista previa: abre la ficha de este visitante al cargar. */
  initialJourneyVisitor?: string;
}

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp', facebook: 'Facebook', instagram: 'Instagram', google: 'Google', linkedin: 'LinkedIn',
  tiktok: 'TikTok', email: 'Correo', directo: 'Directo (escribieron la liga)', otro: 'Otro sitio',
};

const STEP_LABEL: Record<string, string> = {
  visitantes: 'Entraron al sitio',
  conversaciones: 'Iniciaron conversación desde el sitio',
  registros: 'Se registraron',
  pagos_iniciados: 'Empezaron a pagar',
  pagados: 'Pagaron',
};

const nf = new Intl.NumberFormat('es-MX');
const fmt = (n: number) => nf.format(n || 0);
const pct = (r: number) => `${(r * 100).toLocaleString('es-MX', { maximumFractionDigits: r < 0.1 ? 1 : 0 })} %`;

function todayCivil(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
}
function civilDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }) {
  if (!iso) return '';
  try {
    const d = iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso);
    return d.toLocaleDateString('es-MX', { timeZone: iso.length === 10 ? undefined : TZ, ...opts });
  } catch {
    return iso;
  }
}

function sourceLabel(s: CampaignSource): { title: string; detail: string } {
  const title = CHANNEL_LABEL[s.channel] || s.channel;
  const parts = [s.utm_source && s.utm_source !== s.channel ? s.utm_source : '', s.utm_medium, s.utm_campaign].filter(Boolean);
  const detail = parts.length ? parts.join(' · ') : s.via === 'referrer' && s.referrer_host ? s.referrer_host : s.via === 'utm' ? '' : s.via === 'fbclid' ? 'anuncio de Meta (sin UTM)' : s.via === 'gclid' ? 'anuncio de Google (sin UTM)' : '';
  return { title, detail };
}

function countActive(r: InboxResponse | null, start: string): { n: number; capped: boolean } | null {
  if (!r) return null;
  const t0 = new Date(start).getTime();
  const n = r.items.filter((it) => new Date(it.last_message_at).getTime() >= t0).length;
  return { n, capped: r.items.length >= 500 && n === r.items.length };
}

const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`rounded-xl border border-border bg-card text-card-foreground ${className}`}>{children}</div>
);

const Kpi: React.FC<{ icon: React.ReactNode; label: string; value: string; hint?: string }> = ({ icon, label, value, hint }) => (
  <Card className="p-4 min-w-0">
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
      <span className="text-primary">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-2 text-2xl md:text-3xl font-semibold tabular-nums text-foreground">{value}</div>
    {hint && <div className="mt-1 text-xs text-muted-foreground leading-snug">{hint}</div>}
  </Card>
);

export const CampaignResultsPage: React.FC<Props> = ({ addNotification, loaders = defaultLoaders, initialPeriod = '7', initialJourneyVisitor }) => {
  const [period, setPeriod] = useState<Period>(initialPeriod);
  const [data, setData] = useState<CampaignSummary | null>(null);
  const [orion, setOrion] = useState<OrionConversations>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [journeyVid, setJourneyVid] = useState<string | null>(initialJourneyVisitor ?? null);
  const utmRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const q: CampaignQuery =
      period === 'today' ? { since: todayCivil() } : period === 'launch' ? { since: 'launch' } : { days: Number(period) };
    try {
      const d = await loaders.campaign(q);
      setData(d);
      const [wa, web] = await Promise.all([
        loaders.inbox('whatsapp').catch(() => null),
        loaders.inbox('web').catch(() => null),
      ]);
      const a = countActive(wa, d.window.start);
      const b = countActive(web, d.window.start);
      setOrion(a || b ? { whatsapp: a?.n ?? 0, web: b?.n ?? 0, capped: Boolean(a?.capped || b?.capped) } : null);
    } catch (e: any) {
      setError(e?.message || 'No se pudieron cargar los resultados.');
      addNotification?.({ type: 'error', title: 'Resultados de campaña', message: e?.message });
    } finally {
      setLoading(false);
    }
  }, [period, loaders, addNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  const measuringNote = useMemo(() => {
    if (!data) return '';
    if (!data.measuring_since) {
      return data.first_party_enabled
        ? 'La medición de visitas ya está activa, pero aún no llega la primera visita.'
        : 'La medición de visitas del sitio todavía no está activa para tu organización. Registros y pagos sí son reales (vienen del CRM y de los cobros).';
    }
    if (new Date(data.measuring_since).getTime() > new Date(data.window.start).getTime()) {
      return `Empezamos a medir visitas el ${civilDate(data.measuring_since)}; lo anterior no se registró. Registros y pagos sí incluyen todo el periodo.`;
    }
    return '';
  }, [data]);

  const convTotal = orion ? orion.whatsapp + orion.web : data?.conversations_site.visitors ?? 0;
  const top = data?.funnel[0]?.count || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5 text-foreground">
      {/* Encabezado + periodo */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-semibold">Resultados de campaña</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {data ? (
              <>Páginas <span className="font-mono text-foreground">{data.path_prefix}</span> · {civilDate(data.window.start, { day: 'numeric', month: 'short' })} – {civilDate(data.window.end, { day: 'numeric', month: 'short', year: 'numeric' })}</>
            ) : 'Visitas, conversaciones, registros y pagos de tu campaña.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Periodo">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              aria-pressed={period === p.key}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                period === p.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button type="button" onClick={() => void load()} disabled={loading} title="Actualizar" aria-label="Actualizar"
            className="p-2 rounded-lg border border-border bg-card hover:bg-muted disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <Card className="p-4 border-destructive/30 bg-destructive/10 text-destructive text-sm">{error}</Card>
      )}

      {measuringNote && (
        <div className="flex gap-2 rounded-xl border border-border bg-muted/50 p-3 text-sm text-muted-foreground">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <span>{measuringNote}</span>
        </div>
      )}

      {!data && loading && <Card className="p-10 text-center text-sm text-muted-foreground">Cargando resultados…</Card>}

      {data && (
        <>
          {/* Tarjetas */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi icon={<Users className="h-4 w-4" />} label="Visitantes" value={fmt(data.visitors_unique)}
              hint={`${fmt(data.pageviews)} páginas vistas`} />
            <Kpi icon={<MessageCircle className="h-4 w-4" />} label="Clics a WhatsApp" value={fmt(data.whatsapp_clicks.visitors)}
              hint={data.whatsapp_clicks.events > data.whatsapp_clicks.visitors ? `${fmt(data.whatsapp_clicks.events)} clics en total` : 'personas que tocaron el botón'} />
            <Kpi icon={<MessagesSquare className="h-4 w-4" />} label="Conversaciones" value={`${orion?.capped ? '≥ ' : ''}${fmt(convTotal)}`}
              hint={orion ? `WhatsApp ${fmt(orion.whatsapp)} · chat del sitio ${fmt(orion.web)} (con mensajes en el periodo)` : 'desde el sitio (WhatsApp o chat)'} />
            <Kpi icon={<UserPlus className="h-4 w-4" />} label="Registros" value={fmt(data.registrations.total_dedup)}
              hint={data.registrations.by_source.length ? data.registrations.by_source.map((s) => `${s.source.replace(/_/g, ' ')}: ${s.count}`).join(' · ') : 'sin registros en el periodo'} />
            <Kpi icon={<CreditCard className="h-4 w-4" />} label="Empezaron a pagar" value={fmt(data.payments.started)}
              hint={data.payments.by_status.filter((s) => s.status !== 'paid').map((s) => `${STATUS_LABEL[s.status] || s.status}: ${s.count}`).join(' · ') || 'órdenes de pago abiertas'} />
            <Kpi icon={<BadgeCheck className="h-4 w-4" />} label="Pagaron" value={fmt(data.payments.paid)}
              hint={data.payments.seats_paid ? `${fmt(data.payments.seats_paid)} ${data.payments.seats_paid === 1 ? 'lugar' : 'lugares'}` : 'pagos confirmados'} />
          </div>

          <div className="grid gap-4 lg:grid-cols-5">
            {/* Embudo */}
            <Card className="p-4 md:p-5 lg:col-span-3">
              <h3 className="font-semibold">Embudo</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Cada barra contra la primera; el porcentaje es contra el paso anterior.</p>
              <ol className="mt-4 space-y-3">
                {data.funnel.map((s, i) => {
                  const w = top ? Math.max(2, Math.min(100, (s.count / top) * 100)) : 0;
                  const prev = i > 0 ? data.funnel[i - 1].count : 0;
                  return (
                    <li key={s.step}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="text-foreground">{STEP_LABEL[s.step] || s.step}</span>
                        <span className="tabular-nums shrink-0">
                          <span className="font-semibold">{fmt(s.count)}</span>
                          {i > 0 && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {prev ? pct(s.count / prev) : '—'}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2.5 rounded-full bg-muted overflow-hidden" title={`${STEP_LABEL[s.step] || s.step}: ${fmt(s.count)}`}>
                        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${w}%`, opacity: 1 - i * 0.12 }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
              {data.funnel.some((s, i) => i > 0 && s.count > data.funnel[i - 1].count) && (
                <p className="mt-4 text-xs text-muted-foreground">
                  Registros y pagos cuentan a todas las personas (también las que llegaron por WhatsApp o antes de medir visitas), por eso un paso puede ser mayor que el anterior.
                </p>
              )}
            </Card>

            {/* Por día */}
            <Card className="p-4 md:p-5 lg:col-span-2 min-w-0">
              <h3 className="font-semibold">Visitantes por día</h3>
              {data.per_day.some((d) => d.visitors > 0) ? (
                <ChartBox>
                  {(width) => (
                    <BarChart width={width} height={224} data={data.per_day} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={16}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                        tickFormatter={(d: string) => civilDate(d, { day: 'numeric', month: 'short' })} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={36}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                      <Tooltip
                        cursor={{ fill: 'hsl(var(--muted))' }}
                        contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))', fontSize: 12 }}
                        labelFormatter={(d: string) => civilDate(d, { weekday: 'long', day: 'numeric', month: 'long' })}
                        formatter={(v: number, k: string) => [fmt(v), k === 'visitors' ? 'Visitantes' : 'Páginas vistas']}
                      />
                      <Bar dataKey="visitors" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                    </BarChart>
                  )}
                </ChartBox>
              ) : (
                <EmptyNote text="Sin visitas medidas en este periodo." />
              )}
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-5">
            {/* Fuentes */}
            <Card className="p-4 md:p-5 lg:col-span-3 min-w-0">
              <h3 className="font-semibold flex items-center gap-2"><Globe className="h-4 w-4 text-primary" />¿De dónde vienen?</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Por la liga con la que llegaron la primera vez (UTM) o, si no traía, por el sitio de origen.</p>
              {data.sources.length ? (
                <div className="mt-3 -mx-4 md:mx-0 overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b border-border">
                        <th className="py-2 px-4 md:pl-0 font-medium">Fuente</th>
                        <th className="py-2 px-2 font-medium text-right">Visitantes</th>
                        <th className="py-2 px-2 font-medium text-right">WhatsApp</th>
                        <th className="py-2 px-2 font-medium text-right">Registros</th>
                        <th className="py-2 px-2 font-medium text-right">Pago iniciado</th>
                        <th className="py-2 px-4 md:pr-0 font-medium text-right">Conversión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sources.map((s, i) => {
                        const l = sourceLabel(s);
                        return (
                          <tr key={i} className="border-b border-border/60 last:border-0">
                            <td className="py-2 px-4 md:pl-0">
                              <div className="text-foreground">{l.title}</div>
                              {l.detail && <div className="text-xs text-muted-foreground truncate max-w-[240px]">{l.detail}</div>}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums">{fmt(s.visitors)}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{fmt(s.whatsapp_clicks)}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{fmt(s.form_submits)}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{fmt(s.checkout_starts)}</td>
                            <td className="py-2 px-4 md:pr-0 text-right tabular-nums text-muted-foreground">
                              {s.visitors ? pct(Math.max(s.form_submits, s.conversations) / s.visitors) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="mt-2 px-4 md:px-0 text-xs text-muted-foreground">Conversión = personas que conversaron o se registraron ÷ visitantes de esa fuente.</p>
                </div>
              ) : (
                <EmptyNote text="Aún no hay visitas medidas para saber de dónde vienen." />
              )}
            </Card>

            {/* Top páginas */}
            <Card className="p-4 md:p-5 lg:col-span-2 min-w-0">
              <h3 className="font-semibold">Páginas más vistas</h3>
              {data.top_pages.length ? (
                <ul className="mt-3 space-y-2">
                  {data.top_pages.slice(0, 8).map((p) => (
                    <li key={p.path} className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-mono text-xs text-foreground truncate" title={p.path}>{p.path}</span>
                      <span className="tabular-nums text-muted-foreground shrink-0">{fmt(p.visitors)} pers. · {fmt(p.pageviews)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyNote text="Sin páginas vistas en este periodo." />
              )}
              {data.cta_clicks.top_labels.length > 0 && (
                <>
                  <h4 className="mt-5 text-sm font-semibold">Botones más tocados</h4>
                  <ul className="mt-2 space-y-1.5">
                    {data.cta_clicks.top_labels.slice(0, 5).map((c) => (
                      <li key={c.label} className="flex justify-between gap-3 text-sm">
                        <span className="truncate">{c.label}</span>
                        <span className="tabular-nums text-muted-foreground">{fmt(c.clicks)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>
          </div>

          <AdsPanel ads={data.ads} onBuildLink={() => utmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />

          <div className="grid gap-4 lg:grid-cols-2">
            {loaders.visitors && (
              <CampaignVisitors days={Math.min(90, Math.max(1, data.window.days))} load={loaders.visitors} onOpen={setJourneyVid} />
            )}
            <UtmLinkBuilder ref={utmRef} defaultCampaign={data.sources.find((s) => s.utm_campaign)?.utm_campaign || ''} />
          </div>

          {journeyVid && (
            <JourneyDrawer visitorId={journeyVid} onClose={() => setJourneyVid(null)} loaders={loaders.journey || defaultJourneyLoaders} />
          )}

          <p className="text-xs text-muted-foreground">
            Visitas anónimas medidas por el propio sitio (sin cookies; no cuenta a quien pidió no ser rastreado){data.bots_excluded ? ` · ${fmt(data.bots_excluded)} robots excluidos` : ''}.
            {orion && ' Conversaciones: Orión, clientes con mensajes dentro del periodo.'}
          </p>
        </>
      )}
    </div>
  );
};

const STATUS_LABEL: Record<string, string> = {
  checkout_open: 'en pago', awaiting_payment: 'esperando OXXO', expired: 'vencidas', canceled: 'canceladas', refunded: 'reembolsadas',
};

/** Mide su ancho real (ResizeObserver) y se lo pasa a la gráfica. */
const ChartBox: React.FC<{ children: (width: number) => React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setW(Math.floor(el.getBoundingClientRect().width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return <div ref={ref} className="mt-3 h-56 w-full min-w-0">{w > 0 && children(w)}</div>;
};

const EmptyNote: React.FC<{ text: string }> = ({ text }) => (
  <div className="mt-3 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{text}</div>
);

export default CampaignResultsPage;
